/**
 * Color picker component for event colors
 */

import { Check } from 'lucide-react'
import { cn } from '@/utils/misc'
import { CALENDAR_COLORS } from '@/lib/constants'

interface ColorPickerProps {
	value: string
	onChange: (color: string) => void
}

/**
 * Normalize color to lowercase hex format for comparison
 * Handles both hex (#616161) and rgb (rgb(97, 97, 97)) formats
 */
function normalizeColor(color: string): string {
	if (!color) return ''

	// Already hex format
	if (color.startsWith('#')) {
		return color.toLowerCase()
	}

	// RGB format: rgb(r, g, b)
	const rgbMatch = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i)
	if (rgbMatch) {
		const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0')
		const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0')
		const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0')
		return `#${r}${g}${b}`
	}

	return color.toLowerCase()
}

export function ColorPicker({ value, onChange }: ColorPickerProps) {
	const normalizedValue = normalizeColor(value)

	return (
		<div className="flex flex-wrap gap-2">
			{CALENDAR_COLORS.map((colorOption) => {
				const isSelected = normalizedValue === normalizeColor(colorOption.color)
				return (
					<button
						key={colorOption.id}
						type="button"
						title={colorOption.label}
						onClick={() => onChange(colorOption.color)}
						className={cn(
							'relative h-8 w-8 rounded-full transition-transform hover:scale-110',
							isSelected && 'ring-2 ring-offset-2 ring-blue-500',
						)}
						style={{ backgroundColor: colorOption.color }}
					>
						{isSelected && (
							<Check
								className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-white"
								strokeWidth={3}
							/>
						)}
					</button>
				)
			})}
		</div>
	)
}
